import { useState } from "react";
import api, { errorMessage } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import { CATALOG_READ_KEYS } from "../lib/training";
import useJamoraCatalog from "../hooks/useJamoraCatalog";
import FeaturePicker from "./training/FeaturePicker";
import { errorInfo } from "../lib/collaborators";
import { LEVEL_CODES, buildFunctionPatch, buildSkillPatch, isExpectedSkillsValid } from "../lib/skills";
import { StatePill } from "./CollaboratorBits";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Skeleton } from "./ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import DeleteWithReasonDialog from "./DeleteWithReasonDialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { AlertCircle, Loader2, Pencil, Plus, Power, Trash2, X } from "lucide-react";

const NONE = "__none__";

// Fonctions métier, compétences et niveaux, dans la page Organisation. Une fonction ne donne aucun droit :
// son rôle suggéré n'est qu'une indication pour l'administrateur.

function useRun(refs) {
  const [busy, setBusy] = useState(false);
  const run = async (request, success) => {
    setBusy(true);
    try {
      await request();
      toast.success(success);
      await refs.reload();
      return true;
    } catch (error) {
      const info = errorInfo(error);
      toast.error(info.data?.msg || errorMessage(error));
      if (info.code === "version_conflict") await refs.reload();
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { busy, run };
}

function PanelState({ refs, children }) {
  if (refs.status === "loading") return <Skeleton className="h-40 w-full" />;
  if (refs.status === "error") {
    return (
      <Card className="p-8 text-center space-y-3">
        <AlertCircle className="mx-auto text-slate-300" />
        <p className="text-sm text-slate-600">Impossible de charger ce référentiel. Réessayez dans quelques instants.</p>
        <Button variant="outline" onClick={refs.reload}>Réessayer</Button>
      </Card>
    );
  }
  return children;
}

function ToggleDialog({ item, open, onOpenChange, busy, onConfirm, what }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{item?.active ? `Désactiver « ${item?.label} » ?` : `Réactiver « ${item?.label} » ?`}</DialogTitle>
          <DialogDescription>
            {item?.active
              ? `Les fiches qui ont déjà cette ${what} la conservent, mais elle ne sera plus proposée pour de nouvelles attributions. Vous pourrez la réactiver.`
              : "Elle pourra de nouveau être attribuée."}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Annuler</Button>
          <Button onClick={onConfirm} disabled={busy}  data-testid="btn-skillref-toggle-confirm">
            {busy && <Loader2 size={14} className="animate-spin mr-1.5" />} {item?.active ? "Désactiver" : "Réactiver"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RowActions({ item, canManage, onEdit, onToggle, onDelete = null, testid }) {
  if (!canManage) return null;
  return (
    <div className="flex gap-1 shrink-0">
      <Button variant="ghost" size="icon" aria-label={`Modifier ${item.label}`} onClick={onEdit} data-testid={`btn-${testid}-edit-${item.code}`}><Pencil size={15} /></Button>
      <Button variant="ghost" size="icon" aria-label={`${item.active ? "Désactiver" : "Réactiver"} ${item.label}`} onClick={onToggle} data-testid={`btn-${testid}-toggle-${item.code}`}><Power size={15} /></Button>
      {onDelete && <Button variant="ghost" size="icon" className="text-red-700" aria-label={`Supprimer ${item.label}`} onClick={onDelete} data-testid={`btn-${testid}-delete-${item.code}`}><Trash2 size={15} /></Button>}
    </div>
  );
}

function useToggle(refs, path, labels) {
  const { busy, run } = useRun(refs);
  const [item, setItem] = useState(null);
  const [open, setOpen] = useState(false); // separate from `item`: the dialog keeps its text while it fades out
  const ask = (target) => { setItem(target); setOpen(true); };
  const confirm = async () => {
    const ok = await run(() => api.patch(`${path}/${item.id}`, { expected_version: item.version, active: !item.active }), item.active ? labels.off : labels.on);
    if (ok) setOpen(false);
  };
  return { item, open, setOpen, ask, confirm, busy };
}

// Mise à la corbeille d'une compétence ou d'une fonction (Ordre 28-4) : motif obligatoire ; refusée par le serveur si elle sert encore.
function useTrash(refs, path, success) {
  const { user } = useAuth();
  const { run } = useRun(refs);
  const [item, setItem] = useState(null);
  const allowed = hasPermission(user, "skills:delete");
  const confirm = async (reason) => {
    const target = item;
    setItem(null);
    await run(() => api.delete(`${path}/${target.id}`, { data: { reason } }), success);
  };
  return { item, ask: allowed ? setItem : null, close: () => setItem(null), confirm };
}

function TrashDialog({ trash, what }) {
  return (
    <DeleteWithReasonDialog
      target={trash.item} testId={`delete-${what.testid}`} onClose={trash.close} onConfirm={trash.confirm}
      title={`Supprimer ${what.article} « ${trash.item?.label || ""} » ?`}
      description={`${what.sentence} Elle va dans la corbeille et se restaure pendant 30 jours. Si elle sert encore, la suppression est refusée : désactivez-la à la place.`}
    />
  );
}

// --- Fonctions métier ---------------------------------------------------------------------------------------------------
const EMPTY_FUNCTION = { label: "", description: "", expected_skills: [], suggested_role: "" };

export function JobFunctionsPanel({ refs, canManage }) {
  const { busy, run } = useRun(refs);
  const toggle = useToggle(refs, "/job-functions", { off: "Fonction désactivée", on: "Fonction réactivée" });
  const trash = useTrash(refs, "/job-functions", "Fonction mise à la corbeille");
  const [editing, setEditing] = useState(null); // null | "new" | function
  const [draft, setDraft] = useState(EMPTY_FUNCTION);

  const open = (fn) => {
    setEditing(fn);
    setDraft(fn === "new" ? EMPTY_FUNCTION : {
      label: fn.label, description: fn.description || "", expected_skills: fn.expected_skills || [], suggested_role: fn.suggested_role || "",
    });
  };
  const setExpected = (index, patch) => setDraft((d) => ({ ...d, expected_skills: d.expected_skills.map((e, i) => (i === index ? { ...e, ...patch } : e)) }));
  const addExpected = () => setDraft((d) => ({ ...d, expected_skills: [...d.expected_skills, { skill_id: "", level: "autonomous" }] }));
  const removeExpected = (index) => setDraft((d) => ({ ...d, expected_skills: d.expected_skills.filter((_, i) => i !== index) }));

  const valid = draft.label.trim() && isExpectedSkillsValid(draft.expected_skills);
  const save = async () => {
    const ok = editing === "new"
      ? await run(() => api.post("/job-functions", {
        label: draft.label.trim(), description: draft.description.trim() || null, expected_skills: draft.expected_skills, suggested_role: draft.suggested_role || null,
      }), "Fonction métier créée")
      : await run(() => api.patch(`/job-functions/${editing.id}`, buildFunctionPatch(editing, draft)), "Fonction métier mise à jour");
    if (ok) setEditing(null);
  };

  // a skill already expected stays selectable even if it was deactivated since; new rows only offer active ones
  const skillChoices = (current) => refs.skills.filter((s) => s.active || s.id === current);

  return (
    <PanelState refs={refs}>
      <div className="space-y-3" data-testid="job-functions-panel">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm text-slate-500">Ce que font les personnes chez Jamora. Une fiche peut en avoir plusieurs ; une fonction ne donne aucun accès au CRM.</p>
          {canManage && (
            <Button onClick={() => open("new")} className={`shrink-0`} data-testid="btn-job-function-new">
              <Plus size={16} className="mr-1.5" /> Nouvelle fonction
            </Button>
          )}
        </div>
        {refs.functions.length === 0 ? (
          <Card className="p-8 text-center space-y-2" data-testid="job-functions-empty">
            <p className="font-medium text-slate-900">Aucune fonction métier pour le moment</p>
            <p className="text-sm text-slate-500">Créez par exemple « Agent polyvalent » ou « Formateur POS », puis indiquez les compétences attendues.</p>
          </Card>
        ) : (
          <Card className="p-0 divide-y divide-slate-100">
            {refs.functions.map((fn) => (
              <div key={fn.id} className="flex items-start justify-between gap-3 p-4" data-testid={`job-function-row-${fn.code}`}>
                <div className="min-w-0 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap"><span className="font-medium text-slate-900 break-words">{fn.label}</span><StatePill active={fn.active} activeLabel="Active" inactiveLabel="Inactive" /></div>
                  {fn.description && <p className="text-xs text-slate-600 break-words">{fn.description}</p>}
                  <div className="text-xs text-slate-500">
                    {fn.expected_skills.length === 0 ? "Aucune compétence attendue" : fn.expected_skills.map((e) => `${refs.skillLabel(e.skill_id)} (${refs.levelLabel(e.level)})`).join(" · ")}
                    {fn.suggested_role_label && <> · Rôle suggéré : {fn.suggested_role_label}</>}
                  </div>
                </div>
                <RowActions item={fn} canManage={canManage} onEdit={() => open(fn)} onToggle={() => toggle.ask(fn)} onDelete={trash.ask ? () => trash.ask(fn) : null} testid="job-function" />
              </div>
            ))}
          </Card>
        )}

        <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
          <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="job-function-dialog">
            <DialogHeader><DialogTitle>{editing === "new" ? "Nouvelle fonction métier" : "Modifier la fonction métier"}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2"><Label htmlFor="jf-label">Libellé *</Label>
                <Input id="jf-label" className="text-base md:text-sm" maxLength={120} value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} data-testid="input-job-function-label" /></div>
              <div className="space-y-2"><Label htmlFor="jf-desc">Description</Label>
                <Textarea id="jf-desc" rows={2} maxLength={2000} className="text-base md:text-sm" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></div>
              <div className="space-y-2">
                <Label>Compétences attendues</Label>
                {draft.expected_skills.length === 0 && <p className="text-xs text-slate-500">Aucune pour le moment.</p>}
                {draft.expected_skills.map((item, index) => (
                  // grid with minmax(0, …): a long skill name truncates instead of widening the dialog; on a phone the level goes below
                  <div key={index} className="grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[minmax(0,1fr)_9rem_auto] gap-2 items-center min-w-0" data-testid="expected-skill-row">
                    <Select value={item.skill_id || undefined} onValueChange={(v) => setExpected(index, { skill_id: v })}>
                      <SelectTrigger className="col-span-2 sm:col-span-1 min-w-0" aria-label="Compétence" data-testid={`select-expected-skill-${index}`}><SelectValue placeholder="Choisir une compétence" /></SelectTrigger>
                      <SelectContent>{skillChoices(item.skill_id).map((s) => <SelectItem key={s.id} value={s.id}>{s.label}{s.active ? "" : " (inactive)"}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={item.level} onValueChange={(v) => setExpected(index, { level: v })}>
                      <SelectTrigger className="min-w-0" aria-label="Niveau attendu" data-testid={`select-expected-level-${index}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{LEVEL_CODES.map((code) => <SelectItem key={code} value={code}>{refs.levelLabel(code)}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button type="button" variant="ghost" size="icon" aria-label="Retirer cette compétence" onClick={() => removeExpected(index)}><X size={15} /></Button>
                  </div>
                ))}
                {refs.skills.some((s) => s.active) ? (
                  <Button type="button" variant="outline" size="sm" onClick={addExpected} data-testid="btn-expected-skill-add"><Plus size={14} className="mr-1.5" /> Ajouter une compétence</Button>
                ) : (
                  <p className="text-xs text-slate-500">Créez d'abord des compétences dans l'onglet « Compétences ».</p>
                )}
                {!isExpectedSkillsValid(draft.expected_skills) && <p className="text-xs text-amber-700">Choisissez chaque compétence une seule fois.</p>}
              </div>
              <div className="space-y-2">
                <Label>Rôle d'accès suggéré</Label>
                <Select value={draft.suggested_role || NONE} onValueChange={(v) => setDraft({ ...draft, suggested_role: v === NONE ? "" : v })}>
                  <SelectTrigger data-testid="select-suggested-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Aucun</SelectItem>
                    {refs.roles.map((r) => <SelectItem key={r.name} value={r.name}>{r.label}</SelectItem>)}
                    {draft.suggested_role && !refs.roles.some((r) => r.name === draft.suggested_role) && <SelectItem value={draft.suggested_role}>Rôle supprimé</SelectItem>}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">Simple indication pour l'administrateur quand il crée le compte d'une personne. Elle n'est jamais appliquée automatiquement.</p>
              </div>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setEditing(null)} disabled={busy}>Annuler</Button>
              <Button onClick={save} disabled={!valid || busy}  data-testid="btn-job-function-save">
                {busy && <Loader2 size={14} className="animate-spin mr-1.5" />} Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <ToggleDialog item={toggle.item} open={toggle.open} onOpenChange={toggle.setOpen} busy={toggle.busy} onConfirm={toggle.confirm} what="fonction" />
        <TrashDialog trash={trash} what={{ testid: "job-function", article: "la fonction", sentence: "Une fonction portée par une fiche ne peut pas être supprimée." }} />
      </div>
    </PanelState>
  );
}

// --- Compétences --------------------------------------------------------------------------------------------------------
const EMPTY_SKILL = { label: "", domain: "", description: "", criteria: "", feature_codes: [] };

export function SkillsPanel({ refs, canManage }) {
  const { user } = useAuth();
  // The features a skill aims at come from the training catalogue (Ordre 11): shown and chosen only when it is on and readable.
  const catalog = useJamoraCatalog(!!user?.features?.training && hasPermission(user, CATALOG_READ_KEYS));
  const withFeatures = catalog.status === "ready";
  const { busy, run } = useRun(refs);
  const toggle = useToggle(refs, "/skills", { off: "Compétence désactivée", on: "Compétence réactivée" });
  const trash = useTrash(refs, "/skills", "Compétence mise à la corbeille");
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(EMPTY_SKILL);

  const open = (skill) => {
    setEditing(skill);
    setDraft(skill === "new" ? EMPTY_SKILL : {
      label: skill.label, domain: skill.domain || "", description: skill.description || "", criteria: skill.criteria || "", feature_codes: skill.feature_codes || [],
    });
  };
  const save = async () => {
    const body = { label: draft.label.trim(), domain: draft.domain.trim() || null, description: draft.description.trim() || null, criteria: draft.criteria.trim() || null };
    if (withFeatures && draft.feature_codes.length > 0) body.feature_codes = draft.feature_codes;
    const ok = editing === "new"
      ? await run(() => api.post("/skills", body), "Compétence créée")
      : await run(() => api.patch(`/skills/${editing.id}`, buildSkillPatch(editing, draft, { withFeatures })), "Compétence mise à jour");
    if (ok) setEditing(null);
  };
  const field = (key) => ({ value: draft[key], onChange: (e) => setDraft({ ...draft, [key]: e.target.value }) });

  return (
    <PanelState refs={refs}>
      <div className="space-y-3" data-testid="skills-panel">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm text-slate-500">Les savoir-faire que vous suivez : gestion du stock, caisse, formation… Une compétence désactivée reste sur les fiches qui l'ont.</p>
          {canManage && (
            <Button onClick={() => open("new")} className={`shrink-0`} data-testid="btn-skill-new">
              <Plus size={16} className="mr-1.5" /> Nouvelle compétence
            </Button>
          )}
        </div>
        {refs.skills.length === 0 ? (
          <Card className="p-8 text-center space-y-2" data-testid="skills-empty">
            <p className="font-medium text-slate-900">Aucune compétence pour le moment</p>
            <p className="text-sm text-slate-500">Ajoutez les savoir-faire dont vous voulez suivre le niveau dans l'équipe.</p>
          </Card>
        ) : (
          <Card className="p-0 divide-y divide-slate-100">
            {refs.skills.map((skill) => (
              <div key={skill.id} className="flex items-start justify-between gap-3 p-4" data-testid={`skill-row-${skill.code}`}>
                <div className="min-w-0 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-slate-900 break-words">{skill.label}</span>
                    {skill.domain && <span className="text-xs text-slate-500">{skill.domain}</span>}
                    <StatePill active={skill.active} activeLabel="Active" inactiveLabel="Inactive" />
                  </div>
                  {skill.description && <p className="text-xs text-slate-600 break-words">{skill.description}</p>}
                  {skill.criteria && <p className="text-xs text-slate-500 break-words">Critères : {skill.criteria}</p>}
                  {skill.feature_codes?.length > 0 && (
                    <p className="text-xs text-slate-500 break-words" data-testid={`skill-features-${skill.code}`}>
                      Fonctionnalités Jamora : {skill.feature_codes.map((code) => catalog.featureLabel(code)).join(" · ")}
                    </p>
                  )}
                </div>
                <RowActions item={skill} canManage={canManage} onEdit={() => open(skill)} onToggle={() => toggle.ask(skill)} onDelete={trash.ask ? () => trash.ask(skill) : null} testid="skill" />
              </div>
            ))}
          </Card>
        )}

        <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
          <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="skill-dialog">
            <DialogHeader><DialogTitle>{editing === "new" ? "Nouvelle compétence" : "Modifier la compétence"}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2"><Label htmlFor="sk-label">Libellé *</Label>
                <Input id="sk-label" className="text-base md:text-sm" maxLength={120} {...field("label")} data-testid="input-skill-label" /></div>
              <div className="space-y-2"><Label htmlFor="sk-domain">Domaine</Label>
                <Input id="sk-domain" className="text-base md:text-sm" maxLength={80} placeholder="Ex. : Stock, Caisse, Formation" {...field("domain")} /></div>
              <div className="space-y-2"><Label htmlFor="sk-desc">Description</Label>
                <Textarea id="sk-desc" rows={2} maxLength={2000} className="text-base md:text-sm" {...field("description")} /></div>
              <div className="space-y-2"><Label htmlFor="sk-criteria">Critères de mesure</Label>
                <Textarea id="sk-criteria" rows={2} maxLength={2000} className="text-base md:text-sm" placeholder="Ce qui permet de dire qu'une personne maîtrise cette compétence" {...field("criteria")} /></div>
              {withFeatures && (
                <div className="space-y-2">
                  <Label>Fonctionnalités Jamora visées</Label>
                  <p className="text-xs text-slate-500">Les fonctionnalités du logiciel Jamora que cette compétence couvre.</p>
                  <FeaturePicker catalog={catalog} value={draft.feature_codes} onChange={(codes) => setDraft({ ...draft, feature_codes: codes })}
                    testid="skill-features" />
                </div>
              )}
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setEditing(null)} disabled={busy}>Annuler</Button>
              <Button onClick={save} disabled={!draft.label.trim() || busy}  data-testid="btn-skill-save">
                {busy && <Loader2 size={14} className="animate-spin mr-1.5" />} Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <ToggleDialog item={toggle.item} open={toggle.open} onOpenChange={toggle.setOpen} busy={toggle.busy} onConfirm={toggle.confirm} what="compétence" />
        <TrashDialog trash={trash} what={{ testid: "skill", article: "la compétence", sentence: "Une compétence déjà évaluée, attendue ou exigée ne peut pas être supprimée." }} />
      </div>
    </PanelState>
  );
}

// --- Niveaux ------------------------------------------------------------------------------------------------------------
export function LevelsPanel({ refs, canManage }) {
  const { busy, run } = useRun(refs);
  const [editing, setEditing] = useState(null);
  const [label, setLabel] = useState("");

  const save = async () => {
    const ok = await run(() => api.patch(`/skill-levels/${editing.code}`, { expected_version: editing.version, label: label.trim() }), "Libellé mis à jour");
    if (ok) setEditing(null);
  };

  return (
    <PanelState refs={refs}>
      <div className="space-y-3" data-testid="levels-panel">
        <p className="text-sm text-slate-500">Les quatre niveaux de compétence, du plus simple au plus avancé. Leur ordre est fixe ; vous pouvez renommer chacun.</p>
        <Card className="p-0 divide-y divide-slate-100">
          {refs.levels.map((level) => (
            <div key={level.code} className="flex items-center justify-between gap-3 p-4" data-testid={`level-row-${level.code}`}>
              <div className="flex items-center gap-3 min-w-0">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-100 text-xs font-semibold text-slate-600 shrink-0" aria-hidden="true">{level.rank}</span>
                <span className="font-medium text-slate-900 break-words">{level.label}</span>
              </div>
              {canManage && (
                <Button variant="ghost" size="icon" aria-label={`Renommer ${level.label}`} onClick={() => { setEditing(level); setLabel(level.label); }} data-testid={`btn-level-edit-${level.code}`}>
                  <Pencil size={15} />
                </Button>
              )}
            </div>
          ))}
        </Card>
        <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Renommer le niveau {editing?.rank}</DialogTitle></DialogHeader>
            <div className="space-y-2"><Label htmlFor="lv-label">Libellé *</Label>
              <Input id="lv-label" className="text-base md:text-sm" maxLength={60} value={label} onChange={(e) => setLabel(e.target.value)} data-testid="input-level-label" /></div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setEditing(null)} disabled={busy}>Annuler</Button>
              <Button onClick={save} disabled={!label.trim() || busy}  data-testid="btn-level-save">
                {busy && <Loader2 size={14} className="animate-spin mr-1.5" />} Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </PanelState>
  );
}
